import { useMutation } from "@apollo/client";
import noop from "lodash/noop";
import { useAmplitude } from "../../../lib/amplitude/Amplitude";
import type { IAssetDiscussionCommentFieldsFragment } from "../../../lib/graphql/fragments/__generated__/AssetDiscussionCommentFields.generated";
import DestroyAssetDiscussionComment from "../../../lib/graphql/mutations/discussions/DestroyAssetDiscussionComment";
import EditAssetDiscussionComment from "../../../lib/graphql/mutations/discussions/EditAssetDiscussionComment";
import LockAssetDiscussionComment from "../../../lib/graphql/mutations/discussions/LockAssetDiscussionComment";
import PinAssetDiscussionComment from "../../../lib/graphql/mutations/discussions/PinAssetDiscussionComment";
import UnlockAssetDiscussionComment from "../../../lib/graphql/mutations/discussions/UnlockAssetDiscussionComment";
import UnpinAssetDiscussionComment from "../../../lib/graphql/mutations/discussions/UnpinAssetDiscussionComment";
import type {
  IDestroyAssetDiscussionCommentMutation,
  IDestroyAssetDiscussionCommentMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/DestroyAssetDiscussionComment.generated";
import type {
  IEditAssetDiscussionCommentMutation,
  IEditAssetDiscussionCommentMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/EditAssetDiscussionComment.generated";
import type {
  ILockAssetDiscussionCommentMutation,
  ILockAssetDiscussionCommentMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/LockAssetDiscussionComment.generated";
import type {
  IPinAssetDiscussionCommentMutation,
  IPinAssetDiscussionCommentMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/PinAssetDiscussionComment.generated";
import type {
  IUnlockAssetDiscussionCommentMutation,
  IUnlockAssetDiscussionCommentMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/UnlockAssetDiscussionComment.generated";
import type {
  IUnpinAssetDiscussionCommentMutation,
  IUnpinAssetDiscussionCommentMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/UnpinAssetDiscussionComment.generated";
import translateTypenameIntoAssetType from "../../../lib/utils/translateTypenameIntoAssetType";
import transformOnDiscussionCommentsRootQuery from "../apollo/transformOnDiscussionCommentsRootQuery";
import transformOnSingleCommentRootQuery from "../apollo/transformOnSingleCommentRootQuery";

export default function useCommentActions(comment: IAssetDiscussionCommentFieldsFragment) {
  const amplitude = useAmplitude();

  const [destroyAssetDiscussionComment, { loading: isDestroyingComment, error: destroyCommentError }] = useMutation<
    IDestroyAssetDiscussionCommentMutation,
    IDestroyAssetDiscussionCommentMutationVariables
  >(DestroyAssetDiscussionComment, {
    onError: noop,
  });

  const [editAssetDiscussionComment, { loading: isEditingComment, error: editCommentError }] = useMutation<
    IEditAssetDiscussionCommentMutation,
    IEditAssetDiscussionCommentMutationVariables
  >(EditAssetDiscussionComment, {
    onError: noop,
  });

  const [lockAssetDiscussionComment, { loading: isLockingComment, error: lockCommentError }] = useMutation<
    ILockAssetDiscussionCommentMutation,
    ILockAssetDiscussionCommentMutationVariables
  >(LockAssetDiscussionComment, {
    onError: noop,
  });

  const [unlockAssetDiscussionComment, { loading: isUnlockingComment, error: unlockCommentError }] = useMutation<
    IUnlockAssetDiscussionCommentMutation,
    IUnlockAssetDiscussionCommentMutationVariables
  >(UnlockAssetDiscussionComment, {
    onError: noop,
  });

  const [pinAssetDiscussionComment, { loading: isPinningComment, error: pinCommentError }] = useMutation<
    IPinAssetDiscussionCommentMutation,
    IPinAssetDiscussionCommentMutationVariables
  >(PinAssetDiscussionComment, {
    onError: noop,
  });

  const [unpinAssetDiscussionComment, { loading: isUnpinningComment, error: unpinCommentError }] = useMutation<
    IUnpinAssetDiscussionCommentMutation,
    IUnpinAssetDiscussionCommentMutationVariables
  >(UnpinAssetDiscussionComment, {
    onError: noop,
  });

  return {
    destroyCommentError: destroyCommentError || null,
    isDestroyingComment,
    destroyComment: async () => {
      if (!comment.viewerCanDestroy) {
        return;
      }

      amplitude.logEvent("Destroyed a comment", {
        assetId: comment.asset._id,
        assetType: translateTypenameIntoAssetType(comment.asset.__typename),
        contentCharacterCount: comment.bodyAsPlainText.length,
        postedAt: comment.postedAt,
      });

      await destroyAssetDiscussionComment({
        variables: {
          commentId: comment._id,
        },
        update: (dataProxy) => {
          transformOnDiscussionCommentsRootQuery(dataProxy, comment.asset._id, (rootQueryData) => {
            const nodesAfter = rootQueryData.nodes.filter((node) => {
              // keep all the other comments
              return node._id !== comment._id;
            });

            return {
              ...rootQueryData,
              totalCount:
                nodesAfter.length < rootQueryData.nodes.length
                  ? Math.max(rootQueryData.totalCount - 1, 0)
                  : rootQueryData.totalCount,
              nodes: nodesAfter,
            };
          });

          transformOnSingleCommentRootQuery(dataProxy, comment.shortId, () => null);
        },
      });
    },

    editCommentError: editCommentError || null,
    isEditingComment,
    editComment: async (bodyAsPlainText: string) => {
      if (comment.viewerCanEdit) {
        amplitude.logEvent("Edited a comment", {
          assetId: comment.asset._id,
          assetType: translateTypenameIntoAssetType(comment.asset.__typename),
          contentCharacterCount: bodyAsPlainText.length,
        });

        await editAssetDiscussionComment({
          variables: {
            commentId: comment._id,
            bodyAsPlainText,
          },
        });
      }
    },

    lockCommentError: lockCommentError || null,
    isLockingComment,
    lockComment: async () => {
      await lockAssetDiscussionComment({
        variables: {
          commentId: comment._id,
        },
      });
    },

    unlockCommentError: unlockCommentError || null,
    isUnlockingComment,
    unlockComment: async () => {
      await unlockAssetDiscussionComment({
        variables: {
          commentId: comment._id,
        },
      });
    },

    pinCommentError: pinCommentError || null,
    isPinningComment,
    pinComment: async () => {
      await pinAssetDiscussionComment({
        variables: {
          commentId: comment._id,
        },
      });
    },

    unpinCommentError: unpinCommentError || null,
    isUnpinningComment,
    unpinComment: async () => {
      await unpinAssetDiscussionComment({
        variables: {
          commentId: comment._id,
        },
      });
    },
  };
}
